import {
  Form,
  required,
  useCreate,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { AutocompleteCompanyInput } from "@/components/atomic-crm/companies/AutocompleteCompanyInput";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/atomic-crm/misc/ResponsiveDialog";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import type { Deal } from "@/components/atomic-crm/types";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toIsoDay } from "./dates";
import { dealDraftFromOpportunity, type DealDraft } from "./opportunities";
import type { MarketOpportunity } from "./types";

interface ConvertToDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: MarketOpportunity;
}

type DealFormValues = DealDraft & { company_id?: Identifier | null };

/**
 * "Convertir en trato": a short deal form prefilled from the opportunity
 * (name, amount = estimated value, first pipeline stage, closing date). The
 * company is required by the deals pipeline, so it is asked here (an existing
 * one or a new one created inline). On save the deal is created at the end of
 * its stage column and its id is stored in the opportunity's deal_id.
 */
export const ConvertToDealDialog = ({
  open,
  onOpenChange,
  opportunity,
}: ConvertToDealDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const { dealStages } = useConfigurationContext();
  const [create] = useCreate<Deal>();
  const [update] = useUpdate<MarketOpportunity>();
  const [saving, setSaving] = useState(false);
  const firstStage = dealStages[0]?.value ?? "opportunity";
  const draft = dealDraftFromOpportunity(
    opportunity,
    firstStage,
    toIsoDay(new Date()),
  );

  const submit = async (values: Record<string, unknown>) => {
    const data = values as Partial<DealFormValues>;
    setSaving(true);
    try {
      // Append to the end of the stage column of the deals kanban.
      const { data: last } = await dataProvider.getList<Deal>("deals", {
        filter: { stage: data.stage ?? firstStage },
        sort: { field: "index", order: "DESC" },
        pagination: { page: 1, perPage: 1 },
      });
      const deal = await create(
        "deals",
        {
          data: {
            name: String(data.name ?? "").trim(),
            company_id: data.company_id ?? undefined,
            amount: Number(data.amount) || 0,
            stage: data.stage ?? firstStage,
            expected_closing_date:
              data.expected_closing_date ?? draft.expected_closing_date,
            description: data.description ?? "",
            sales_id: identity?.id,
            contact_ids: [],
            categories: [],
            index: last.length ? Number(last[0].index) + 1 : 0,
          },
        },
        { returnPromise: true },
      );
      if (!deal) throw new Error("deal not created");
      await update(
        "market_opportunities",
        {
          id: opportunity.id,
          data: { deal_id: deal.id },
          previousData: opportunity,
        },
        { returnPromise: true },
      );
      notify("market.opportunities.convert.done", { type: "success" });
      onOpenChange(false);
    } catch {
      notify("market.opportunities.convert.error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const title = translate("market.opportunities.convert.title");

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        mobileTitle={title}
        className="sm:max-w-xl md:max-h-[90dvh] md:overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold max-md:sr-only">
            {title}
          </DialogTitle>
          <DialogDescription>
            {translate("market.opportunities.convert.intro")}
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <Form
            defaultValues={draft}
            onSubmit={submit}
            className="flex flex-col gap-4"
          >
            <TextInput
              source="name"
              label="resources.deals.fields.name"
              validate={required()}
              helperText={false}
            />
            <ReferenceInput source="company_id" reference="companies">
              <AutocompleteCompanyInput
                label="resources.deals.fields.company_id"
                validate={required()}
                modal
              />
            </ReferenceInput>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <NumberInput
                source="amount"
                label="resources.deals.fields.amount"
                validate={required()}
                helperText={false}
              />
              <DateInput
                source="expected_closing_date"
                label="resources.deals.fields.expected_closing_date"
                validate={required()}
                helperText={false}
              />
            </div>
            <SelectInput
              source="stage"
              label="resources.deals.fields.stage"
              choices={dealStages}
              optionText="label"
              optionValue="value"
              validate={required()}
              helperText={false}
            />
            <TextInput
              source="description"
              label="resources.deals.fields.description"
              multiline
              rows={3}
              helperText={false}
            />
            <DialogFooter className="flex-row flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-full max-md:hidden"
                onClick={() => onOpenChange(false)}
              >
                {translate("market.opportunities.form.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-save rounded-full px-5 max-md:h-12 max-md:flex-1"
              >
                {translate("market.opportunities.convert.submit")}
              </Button>
            </DialogFooter>
          </Form>
        ) : null}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
